import React, { useEffect, useState } from 'react';
import { recurring as recurApi } from '@/utils/api';
import { Plus, RefreshCw, Trash2, Play, X, Pause, Calendar as CalIcon, Tag, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import CatalogPicker from '@/components/CatalogPicker';

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

function formatNext(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d - now;
  const days = Math.ceil(diffMs / (24 * 3600 * 1000));
  return `${d.toLocaleDateString()} ${days >= 0 ? `(in ${days}d)` : '(overdue)'}`;
}

export default function RecurringInvoicesTab({ projectId }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {draft} or 'new'
  const blank = {
    name: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0, notes: '', category: '', catalog_item_id: null }],
    currency: 'USD', tax_rate: 0, discount: 0,
    payment_methods: ['bank_transfer'],
    notes: '',
    frequency: 'monthly',
    interval: 1,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: '',
    is_active: true,
  };
  const [draft, setDraft] = useState(blank);

  const reload = () => recurApi.listForProject(projectId)
    .then(r => setList(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId]);

  const openNew = () => { setDraft(blank); setEditing('new'); };
  const openEdit = (rec) => {
    setDraft({
      ...rec,
      line_items: rec.line_items?.length ? rec.line_items : blank.line_items,
      start_date: rec.next_run_at?.slice(0, 10) || blank.start_date,
      end_date: rec.end_date || '',
    });
    setEditing(rec.id);
  };

  const save = async () => {
    if (!draft.name?.trim()) return toast.error('Name required');
    if (!draft.line_items?.length) return toast.error('Add at least one line');
    try {
      const payload = { ...draft, project_id: projectId };
      if (editing === 'new') {
        await recurApi.create(payload);
        toast.success('Template created');
      } else {
        // Backend update doesn't accept project_id / client_id — strip them
        const { project_id, client_id, brand_id, id, ...upd } = payload;
        await recurApi.update(editing, upd);
        toast.success('Template updated');
      }
      setEditing(null);
      reload();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x.msg).join(', ') : 'Failed');
    }
  };

  const toggleActive = async (rec) => {
    await recurApi.update(rec.id, { is_active: !rec.is_active });
    reload();
  };

  const runNow = async (rec) => {
    if (!window.confirm(`Generate a Draft invoice now from "${rec.name}"?`)) return;
    try {
      await recurApi.runNow(rec.id);
      toast.success('Draft invoice generated — check Invoices tab');
      reload();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete recurring template? Previously generated invoices are kept.')) return;
    await recurApi.delete(id);
    reload();
  };

  const updateLine = (i, field, val) => {
    const next = [...draft.line_items];
    next[i] = { ...next[i], [field]: ['description', 'notes', 'category'].includes(field) ? val : Number(val) };
    if (['quantity', 'unit_price'].includes(field)) next[i].amount = next[i].quantity * next[i].unit_price;
    setDraft({ ...draft, line_items: next });
  };
  const pickCatalog = (i, item) => {
    const next = [...draft.line_items];
    next[i] = {
      ...next[i],
      description: item.name + (item.description ? ` — ${item.description}` : ''),
      unit_price: item.default_price || next[i].unit_price,
      quantity: item.default_hours || next[i].quantity || 1,
      category: item.category || '',
      catalog_item_id: item.id || null,
    };
    next[i].amount = next[i].quantity * next[i].unit_price;
    setDraft({ ...draft, line_items: next });
  };
  const addLine = () => setDraft({
    ...draft,
    line_items: [...draft.line_items, { description: '', quantity: 1, unit_price: 0, amount: 0, notes: '', category: '', catalog_item_id: null }],
  });
  const removeLine = (i) => {
    if (draft.line_items.length <= 1) return;
    setDraft({ ...draft, line_items: draft.line_items.filter((_, idx) => idx !== i) });
  };

  const subtotal = draft.line_items.reduce((s, li) => s + (li.amount || li.quantity * li.unit_price || 0), 0);
  const tax = subtotal * (draft.tax_rate / 100);
  const total = subtotal + tax - (draft.discount || 0);

  return (
    <div className="animate-fade-in" data-testid="recurring-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Recurring Invoices</h3>
        <button className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" onClick={openNew} data-testid="new-recurring-button">
          <Plus size={13} /> New Template
        </button>
      </div>

      {loading ? (
        <div className="h-20 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />
      ) : list.length === 0 ? (
        <div className="card-base p-8 text-center">
          <RefreshCw size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No recurring invoices</p>
          <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Create a template to auto-generate invoices on a schedule.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="recurring-list">
          {list.map(r => (
            <div key={r.id} className="card-base p-4" data-testid={`recurring-${r.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{r.name}</p>
                    <span className="badge-status" style={{ background: r.is_active ? '#3C8A5A15' : '#E8E3D9', color: r.is_active ? '#3C8A5A' : '#5C6B72' }}>
                      {r.is_active ? 'Active' : 'Paused'}
                    </span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>
                    Every {r.interval} {r.frequency} · Next: {formatNext(r.next_run_at)} · Generated: {r.invoices_generated || 0}
                  </p>
                </div>
                <p className="text-lg font-black" style={{ color: 'var(--ink)' }}>{r.currency} {r.total || 0}</p>
              </div>
              <div className="space-y-1 mb-3">
                {r.line_items?.map((li, i) => (
                  <div key={i} className="py-1 border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-center justify-between text-sm">
                      <span style={{ color: 'var(--ink)' }} className="flex items-center gap-2">
                        {li.description}
                        {li.category && <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: '#E6F1F4', color: '#006080' }}><Tag size={8} style={{ display: 'inline' }} /> {li.category}</span>}
                      </span>
                      <span style={{ color: 'var(--muted)' }}>{r.currency} {(li.amount || li.quantity * li.unit_price)?.toLocaleString()}</span>
                    </div>
                    {li.notes && <p className="text-xs mt-0.5 flex items-start gap-1" style={{ color: 'var(--muted)' }}><MessageSquare size={10} className="mt-0.5 flex-shrink-0" />{li.notes}</p>}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => runNow(r)} className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#E6F1F4', color: '#006080' }} data-testid={`run-now-${r.id}`}>
                  <Play size={11} /> Run now
                </button>
                <button onClick={() => toggleActive(r)} className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--border)' }} data-testid={`toggle-${r.id}`}>
                  {r.is_active ? <><Pause size={11} /> Pause</> : <><Play size={11} /> Resume</>}
                </button>
                <button onClick={() => openEdit(r)} className="text-xs px-3 py-1.5 rounded-btn font-semibold" style={{ background: 'var(--surface)', color: 'var(--muted)', border: '1px solid var(--border)' }}>Edit</button>
                <button onClick={() => remove(r.id)} className="text-xs p-1.5 rounded-btn" style={{ color: '#B34A3A' }}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card-base w-full max-w-2xl p-5 overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--ink)' }}>{editing === 'new' ? 'New' : 'Edit'} Recurring Template</h3>
              <button onClick={() => setEditing(null)} style={{ color: 'var(--muted)' }}><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input className="input-agency text-sm" placeholder="Template name (e.g. Monthly Retainer)" value={draft.name} onChange={e => setDraft({...draft, name: e.target.value})} data-testid="recurring-name" />

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Frequency</label>
                  <select className="input-agency text-sm" value={draft.frequency} onChange={e => setDraft({...draft, frequency: e.target.value})}>
                    {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Interval</label>
                  <input className="input-agency text-sm" type="number" min="1" value={draft.interval} onChange={e => setDraft({...draft, interval: Number(e.target.value)})} />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Currency</label>
                  <select className="input-agency text-sm" value={draft.currency} onChange={e => setDraft({...draft, currency: e.target.value})}>
                    {['USD','EUR','GBP','INR','AUD','CAD'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>First invoice date</label>
                  <input className="input-agency text-sm" type="date" value={draft.start_date || ''} onChange={e => setDraft({...draft, start_date: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>End date (optional)</label>
                  <input className="input-agency text-sm" type="date" value={draft.end_date || ''} onChange={e => setDraft({...draft, end_date: e.target.value})} />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Line Items</label>
                  <button type="button" className="text-xs" style={{ color: '#006080' }} onClick={addLine}>+ Add Line</button>
                </div>
                <div className="space-y-3">
                  {draft.line_items.map((li, i) => (
                    <div key={i} className="p-2 rounded-btn" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                      <div className="grid grid-cols-12 gap-1.5 items-center">
                        <input className="input-agency text-sm col-span-6" placeholder="Description" value={li.description} onChange={e => updateLine(i, 'description', e.target.value)} required data-testid={`rec-line-desc-${i}`} />
                        <input className="input-agency text-sm col-span-2 text-center" type="number" step="0.25" min="0" value={li.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} />
                        <input className="input-agency text-sm col-span-2 text-center" type="number" min="0" value={li.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)} />
                        <div className="col-span-1 text-xs tabular-nums text-right font-semibold" style={{ color: 'var(--ink)' }}>{(li.amount || li.quantity * li.unit_price).toFixed(2)}</div>
                        <button type="button" onClick={() => removeLine(i)} className="col-span-1 p-1" style={{ color: draft.line_items.length > 1 ? '#B34A3A' : 'var(--border)' }}><Trash2 size={12} /></button>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <CatalogPicker onPick={(it) => pickCatalog(i, it)} />
                        {li.category && <span className="text-[10px] px-1.5 py-0.5 rounded-sm flex items-center gap-0.5" style={{ background: '#E6F1F4', color: '#006080' }}><Tag size={8} /> {li.category}</span>}
                        <input className="flex-1 text-xs px-2 py-1.5 rounded-btn border outline-none" style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }} placeholder="Remark (optional)" value={li.notes || ''} onChange={e => updateLine(i, 'notes', e.target.value)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Tax %</label><input className="input-agency text-sm" type="number" value={draft.tax_rate} onChange={e => setDraft({...draft, tax_rate: Number(e.target.value)})} /></div>
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Discount</label><input className="input-agency text-sm" type="number" value={draft.discount} onChange={e => setDraft({...draft, discount: Number(e.target.value)})} /></div>
                <div className="flex items-end">
                  <div className="text-right w-full">
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>Total</p>
                    <p className="text-xl font-black" style={{ color: 'var(--ink)' }}>{draft.currency} {total.toFixed(2)}</p>
                  </div>
                </div>
              </div>
              <textarea className="input-agency text-sm resize-none" rows={2} placeholder="Notes (appears on every generated invoice)" value={draft.notes || ''} onChange={e => setDraft({...draft, notes: e.target.value})} />

              <div className="flex gap-3">
                <button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setEditing(null)}>Cancel</button>
                <button type="button" className="flex-1 btn-primary py-2 text-sm" onClick={save} data-testid="save-recurring">Save Template</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
